import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  query,
  where,
  setDoc
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { SkeletonCard } from '../common/SkeletonLoader';
import { sendInvitationEmail, generateFirebaseAuthLink } from '../../services/emailService';
import { seedSystemRoles } from '../../services/seedSystemRoles';
import VideoAllowanceModal from './VideoAllowanceModal';
import {
  BuildingOfficeIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserPlusIcon,
  XMarkIcon,
  UsersIcon,
  ShieldCheckIcon,
  VideoCameraIcon
} from '@heroicons/react/24/outline';

interface Organization {
  id: string;
  name: string;
  slug: string;
  description?: string;
  domain?: string;
  status: 'active' | 'suspended' | 'pending';
  createdAt: any;
  createdBy: string;
  updatedAt?: any;
  settings?: {
    maxManagers?: number;
    maxStudents?: number;
    features?: string[];
  };
  /**
   * Video-avatar sessions — a paid add-on, off unless switched on here.
   * `monthlyMinutes` is the allowance the platform enforces at session start,
   * so an organisation can never spend beyond what it has bought.
   */
  videoSessions?: {
    enabled?: boolean;
    monthlyMinutes?: number;
    maxSessionMinutes?: number;
  };
}

interface OrgAdminInvitation {
  id: string;
  email: string;
  displayName?: string;
  organizationId: string;
  organizationName?: string;
  status: 'pending' | 'accepted' | 'expired';
  createdAt: any;
  expiresAt: string;
  invitedBy: string;
}

interface OrganizationWithAdmins extends Organization {
  admins: OrgAdmin[];
  pendingInvitations: OrgAdminInvitation[];
  adminCount: number;
  pendingCount: number;
}

interface OrgAdmin {
  id: string;
  uid: string;
  email: string;
  displayName: string;
  organization_id: string;
  role: 'org_admin';
  status: 'active' | 'pending' | 'suspended';
  createdAt: any;
  createdBy: string;
  lastLogin?: any;
}

// Join codes use an unambiguous alphabet (no O/0/I/1) so they're easy to read
// aloud and type. Students enter this code to request to join the org — see
// student/api/join-by-code.js.
const JOIN_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const generateJoinCode = (len = 6): string => {
  let code = '';
  for (let i = 0; i < len; i++) {
    code += JOIN_CODE_ALPHABET[Math.floor(Math.random() * JOIN_CODE_ALPHABET.length)];
  }
  return code;
};

// Generates a join code not already used by another org (joinCode must be
// unique so a code resolves to exactly one org).
const generateUniqueJoinCode = async (): Promise<string> => {
  for (let attempt = 0; attempt < 8; attempt++) {
    const code = generateJoinCode();
    const existing = await getDocs(
      query(collection(db, 'organizations'), where('joinCode', '==', code))
    );
    if (existing.empty) return code;
  }
  // Extremely unlikely fallback: widen the space to make a collision negligible.
  return generateJoinCode(8);
};

const OrganizationManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const [organizations, setOrganizations] = useState<OrganizationWithAdmins[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateAdminModal, setShowCreateAdminModal] = useState(false);
  const [showViewAdminsModal, setShowViewAdminsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<OrganizationWithAdmins | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    domain: '',
    maxManagers: 10,
    maxStudents: 100,
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    domain: '',
    maxManagers: 10,
    maxStudents: 100,
  });
  const [adminFormData, setAdminFormData] = useState({
    email: '',
    displayName: '',
    sendInvitation: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);

      // Fetch all organizations
      const orgsSnapshot = await getDocs(collection(db, 'organizations'));
      const orgs: Organization[] = [];
      orgsSnapshot.forEach((doc) => {
        orgs.push({ id: doc.id, ...doc.data() } as Organization);
      });

      // Fetch all org-admins
      const adminsSnapshot = await getDocs(collection(db, 'org-admins'));
      const allAdmins: OrgAdmin[] = [];
      adminsSnapshot.forEach((doc) => {
        allAdmins.push({ id: doc.id, ...doc.data() } as OrgAdmin);
      });

      // Fetch all pending org-admin invitations
      const invitationsSnapshot = await getDocs(collection(db, 'org-admin-invitations'));
      const allInvitations: OrgAdminInvitation[] = [];
      invitationsSnapshot.forEach((doc) => {
        const data = doc.data();
        // Include all pending invitations (both expired and active)
        if (data.status === 'pending') {
          allInvitations.push({ id: doc.id, ...data } as OrgAdminInvitation);
        }
      });

      // Map admins and invitations to organizations
      const orgsWithAdmins: OrganizationWithAdmins[] = orgs.map(org => {
        const orgAdmins = allAdmins.filter(admin => admin.organization_id === org.id);
        const pendingInvitations = allInvitations.filter(invitation => invitation.organizationId === org.id);
        return {
          ...org,
          admins: orgAdmins,
          pendingInvitations: pendingInvitations,
          adminCount: orgAdmins.length,
          pendingCount: pendingInvitations.length
        };
      });

      // Sort by createdAt descending
      orgsWithAdmins.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime;
      });

      setOrganizations(orgsWithAdmins);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      showToast('Failed to load organizations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser?.uid) {
      showToast('You must be logged in to create an organization', 'error');
      return;
    }

    // Validate slug format (lowercase, no spaces, alphanumeric + hyphens)
    const slugRegex = /^[a-z0-9-]+$/;
    if (!slugRegex.test(formData.slug)) {
      showToast('Slug must be lowercase, alphanumeric, and can only contain hyphens', 'error');
      return;
    }

    // Check if slug already exists
    const existingQuery = query(
      collection(db, 'organizations'),
      where('slug', '==', formData.slug)
    );
    const existingSnapshot = await getDocs(existingQuery);
    if (!existingSnapshot.empty) {
      showToast('An organization with this slug already exists', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const joinCode = await generateUniqueJoinCode();
      const newOrg = {
        name: formData.name,
        slug: formData.slug,
        description: formData.description,
        domain: formData.domain,
        joinCode,
        status: 'active' as const,
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid,
        updatedAt: serverTimestamp(),
        settings: {
          maxManagers: formData.maxManagers,
          maxStudents: formData.maxStudents,
          features: ['basic', 'analytics', 'invitations'],
        },
      };

      const newOrgRef = await addDoc(collection(db, 'organizations'), newOrg);
      // Phase 2: seed the 4 system role docs immediately so this org is
      // RBAC-ready. The super-admin can write role docs via the isSuperAdmin()
      // bypass in firestore.rules.
      try {
        await seedSystemRoles(newOrgRef.id);
      } catch (seedErr) {
        console.error(
          `[OrganizationManagement] failed to seed system roles for ${newOrgRef.id}:`,
          seedErr
        );
        // Don't fail the org create on a seed error — the seed script can be
        // re-run later. Log loudly so it's visible during smoke testing.
      }

      showToast('Organization created successfully!', 'success');
      setShowCreateModal(false);
      setSlugManuallyEdited(false);
      setFormData({
        name: '',
        slug: '',
        description: '',
        domain: '',
        maxManagers: 10,
        maxStudents: 100,
      });
      fetchOrganizations();
    } catch (error: any) {
      console.error('Error creating organization:', error);
      showToast(`Failed to create organization: ${error.message}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateOrgAdmin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser?.uid || !selectedOrg) {
      showToast('Missing required information', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const emailLower = adminFormData.email.toLowerCase();

      // Check if email already exists in org-admins
      const existingQuery = query(
        collection(db, 'org-admins'),
        where('email', '==', emailLower)
      );
      const existingSnapshot = await getDocs(existingQuery);

      if (!existingSnapshot.empty) {
        showToast('An org-admin with this email already exists', 'error');
        setSubmitting(false);
        return;
      }

      if (adminFormData.sendInvitation) {
        // Email invitation flow
        // 1. Check if invitation already exists
        const existingInvitationQuery = query(
          collection(db, 'org-admin-invitations'),
          where('email', '==', emailLower),
          where('status', '==', 'pending')
        );
        const existingInvitationSnapshot = await getDocs(existingInvitationQuery);

        if (!existingInvitationSnapshot.empty) {
          showToast('An invitation is already pending for this email address', 'warning');
          setSubmitting(false);
          return;
        }

        // 2. Create invitation record FIRST
        const invitationRef = doc(collection(db, 'org-admin-invitations'));
        const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000); // 7 days

        await setDoc(invitationRef, {
          email: emailLower,
          displayName: adminFormData.displayName,
          organizationId: selectedOrg.id,
          organizationName: selectedOrg.name,
          invitedBy: currentUser.uid,
          invitedAt: serverTimestamp(),
          status: 'pending',
          expiresAt: expiresAt.toISOString()
        });

        // 3. Generate Firebase authentication link using Admin SDK
        const continueUrl = `${import.meta.env.VITE_MANAGER_URL || 'http://localhost:3500'}/org-admin-invitation-handler?email=${encodeURIComponent(emailLower)}`;

        console.log('🔐 Generating Firebase auth link for:', emailLower);
        const authLinkResult = await generateFirebaseAuthLink({
          email: emailLower,
          continueUrl
        });

        if (!authLinkResult.success || !authLinkResult.link) {
          throw new Error(authLinkResult.error || 'Failed to generate authentication link');
        }

        console.log('✅ Firebase auth link generated successfully');

        // 4. Send the invitation email via Resend with the Firebase auth link
        console.log('📧 Sending invitation email via Resend to:', emailLower);
        const emailResult = await sendInvitationEmail({
          email: emailLower,
          inviteLink: authLinkResult.link,
          type: 'org-admin',
          organizationName: selectedOrg.name,
          organizationId: selectedOrg.id,
          senderName:
            currentUser?.displayName ||
            currentUser?.email ||
            'A team member',
          senderEmail: currentUser?.email || undefined,
        });

        if (!emailResult.success) {
          throw new Error(emailResult.error || 'Failed to send invitation email');
        }

        console.log('✅ Invitation email sent successfully via Resend')

        showToast(`Invitation sent successfully to ${emailLower}`, 'success');
      } else {
        // Direct creation without invitation (legacy flow)
        const orgAdminRef = doc(collection(db, 'org-admins'));
        const newOrgAdmin = {
          uid: orgAdminRef.id,
          email: emailLower,
          displayName: adminFormData.displayName,
          organization_id: selectedOrg.id,
          roleKey: 'org_admin',
          role: 'org_admin' as const,
          status: 'pending' as const,
          createdAt: serverTimestamp(),
          createdBy: currentUser.uid,
          updatedAt: serverTimestamp(),
          phone: '',
          photoURL: '',
          lastLogin: null,
        };

        await setDoc(orgAdminRef, newOrgAdmin);
        showToast('Org-admin created successfully!', 'success');
      }

      setShowCreateAdminModal(false);
      setSelectedOrg(null);
      setAdminFormData({
        email: '',
        displayName: '',
        sendInvitation: true,
      });
      fetchOrganizations();
    } catch (error: any) {
      console.error('Error creating org-admin:', error);
      showToast(`Failed to create org-admin: ${error.message}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };


  const handleRemoveAdmin = async (admin: OrgAdmin) => {
    const confirmed = window.confirm(
      `Are you sure you want to remove "${admin.displayName}" (${admin.email}) as an org-admin? They will lose access to manage this organization.`
    );

    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, 'org-admins', admin.id));
      showToast('Org-admin removed successfully!', 'success');
      await fetchOrganizations();

      // Update selected org if viewing admins
      if (selectedOrg) {
        const updatedOrg = organizations.find(org => org.id === selectedOrg.id);
        if (updatedOrg) {
          setSelectedOrg(updatedOrg);
        }
      }
    } catch (error: any) {
      console.error('Error removing org-admin:', error);
      showToast(`Failed to remove org-admin: ${error.message}`, 'error');
    }
  };

  const handleUpdateAdminStatus = async (admin: OrgAdmin, newStatus: 'active' | 'suspended') => {
    try {
      await updateDoc(doc(db, 'org-admins', admin.id), {
        status: newStatus,
        updatedAt: serverTimestamp(),
      });
      showToast(`Admin status updated to ${newStatus}`, 'success');
      await fetchOrganizations();

      // Update selected org if viewing admins
      if (selectedOrg) {
        const updatedOrg = organizations.find(org => org.id === selectedOrg.id);
        if (updatedOrg) {
          setSelectedOrg(updatedOrg);
        }
      }
    } catch (error: any) {
      console.error('Error updating admin status:', error);
      showToast(`Failed to update admin status: ${error.message}`, 'error');
    }
  };

  const handleRemoveInvitation = async (invitation: OrgAdminInvitation) => {
    const confirmed = window.confirm(
      `Are you sure you want to remove the invitation for "${invitation.email}"? They will no longer be able to accept this invitation.`
    );

    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, 'org-admin-invitations', invitation.id));
      showToast('Invitation removed successfully!', 'success');
      await fetchOrganizations();

      // Update selected org if viewing admins
      if (selectedOrg) {
        const updatedOrg = organizations.find(org => org.id === selectedOrg.id);
        if (updatedOrg) {
          setSelectedOrg(updatedOrg);
        }
      }
    } catch (error: any) {
      console.error('Error removing invitation:', error);
      showToast(`Failed to remove invitation: ${error.message}`, 'error');
    }
  };

  const handleResendInvitation = async (invitation: OrgAdminInvitation) => {
    if (!currentUser?.uid) {
      showToast('You must be logged in to resend invitations', 'error');
      return;
    }

    try {
      // Update the expiration date
      const newExpiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000); // 7 days from now
      
      await updateDoc(doc(db, 'org-admin-invitations', invitation.id), {
        expiresAt: newExpiresAt.toISOString(),
        status: 'pending',
        updatedAt: serverTimestamp(),
      });

      // Generate Firebase authentication link using Admin SDK
      const continueUrl = `${import.meta.env.VITE_MANAGER_URL || 'http://localhost:3500'}/org-admin-invitation-handler?email=${encodeURIComponent(invitation.email)}`;

      console.log('🔐 Generating Firebase auth link for:', invitation.email);
      const authLinkResult = await generateFirebaseAuthLink({
        email: invitation.email,
        continueUrl
      });

      if (!authLinkResult.success || !authLinkResult.link) {
        throw new Error(authLinkResult.error || 'Failed to generate authentication link');
      }

      console.log('✅ Firebase auth link generated successfully');

      // Send the invitation email via Resend with the Firebase auth link
      console.log('📧 Resending invitation email via Resend to:', invitation.email);
      const emailResult = await sendInvitationEmail({
        email: invitation.email,
        inviteLink: authLinkResult.link,
        type: 'org-admin',
        organizationName: invitation.organizationName,
        organizationId: invitation.organizationId,
        senderName:
          currentUser?.displayName ||
          currentUser?.email ||
          'A team member',
        senderEmail: currentUser?.email || undefined,
      });

      if (!emailResult.success) {
        throw new Error(emailResult.error || 'Failed to resend invitation email');
      }

      console.log('✅ Invitation email resent successfully via Resend');

      showToast(`Invitation resent successfully to ${invitation.email}`, 'success');
      await fetchOrganizations();

      // Update selected org if viewing admins
      if (selectedOrg) {
        const updatedOrg = organizations.find(org => org.id === selectedOrg.id);
        if (updatedOrg) {
          setSelectedOrg(updatedOrg);
        }
      }
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      showToast(`Failed to resend invitation: ${error.message}`, 'error');
    }
  };

  const handleDeleteOrganization = async (org: OrganizationWithAdmins) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${org.name}"? This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      // Check if there are any org-admins associated with this organization
      const orgAdminsQuery = query(
        collection(db, 'org-admins'),
        where('organization_id', '==', org.id)
      );
      const orgAdminsSnapshot = await getDocs(orgAdminsQuery);

      if (!orgAdminsSnapshot.empty) {
        showToast(`Cannot delete organization: ${orgAdminsSnapshot.size} org-admin(s) are still associated with it. Please remove them first.`, 'warning');
        return;
      }

      await deleteDoc(doc(db, 'organizations', org.id));
      showToast('Organization deleted successfully!', 'success');
      fetchOrganizations();
    } catch (error: any) {
      console.error('Error deleting organization:', error);
      showToast(`Failed to delete organization: ${error.message}`, 'error');
    }
  };


  const openEditModal = (org: OrganizationWithAdmins) => {
    setSelectedOrg(org);
    setEditFormData({
      name: org.name,
      description: org.description || '',
      domain: org.domain || '',
      maxManagers: org.settings?.maxManagers || 10,
      maxStudents: org.settings?.maxStudents || 100,
    });
    setShowEditModal(true);
  };

  const handleUpdateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser?.uid || !selectedOrg) {
      showToast('Missing required information', 'error');
      return;
    }

    setSubmitting(true);
    try {
      await updateDoc(doc(db, 'organizations', selectedOrg.id), {
        name: editFormData.name,
        description: editFormData.description,
        domain: editFormData.domain,
        settings: {
          maxManagers: editFormData.maxManagers,
          maxStudents: editFormData.maxStudents,
          features: selectedOrg.settings?.features || ['basic', 'analytics', 'invitations'],
        },
        updatedAt: serverTimestamp(),
      });

      showToast('Organization updated successfully!', 'success');
      setShowEditModal(false);
      setSelectedOrg(null);
      await fetchOrganizations();
    } catch (error: any) {
      console.error('Error updating organization:', error);
      showToast(`Failed to update organization: ${error.message}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (org: OrganizationWithAdmins, newStatus: 'active' | 'suspended') => {
    try {
      await updateDoc(doc(db, 'organizations', org.id), {
        status: newStatus,
        updatedAt: serverTimestamp(),
      });
      showToast(`Organization status updated to ${newStatus}`, 'success');
      fetchOrganizations();
    } catch (error: any) {
      console.error('Error updating organization:', error);
      showToast(`Failed to update organization: ${error.message}`, 'error');
    }
  };

  // Video allowance is edited in its own dialog: on/off, monthly minutes,
  // per-session cap, and this month's usage with a reset. The row button
  // only opens it.
  const [videoAllowanceOrg, setVideoAllowanceOrg] = useState<OrganizationWithAdmins | null>(null);

  const openCreateAdminModal = (org: OrganizationWithAdmins) => {
    setSelectedOrg(org);
    setShowCreateAdminModal(true);
  };

  const openViewAdminsModal = (org: OrganizationWithAdmins) => {
    setSelectedOrg(org);
    setShowViewAdminsModal(true);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch {
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <div className="flex-1 h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex flex-col transition-colors duration-200 relative overflow-hidden">
        {/* Dark mode accent elements */}
        <div className="absolute inset-0 pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-200">
          <div className="absolute top-20 left-20 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-pink-500/3 rounded-full blur-3xl"></div>
        </div>
        <div className="flex-1 flex flex-col overflow-y-auto bg-transparent">
          <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
            <div className="mb-5">
              <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-1.5" />
              <div className="h-3.5 w-64 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex flex-col transition-colors duration-200 relative overflow-hidden">
      {/* Dark mode accent elements */}
      <div className="absolute inset-0 pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-200">
        <div className="absolute top-20 left-20 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-pink-500/3 rounded-full blur-3xl"></div>
      </div>
      <div className="flex-1 flex flex-col overflow-y-auto bg-transparent">
        <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Organizations</h1>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">Manage organizations and their administrators</p>
        </div>
        <button
          onClick={() => { setShowCreateModal(true); setSlugManuallyEdited(false); }}
          className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-xl font-semibold text-xs transition-all duration-200"
        >
          <PlusIcon className="w-4 h-4" />
          <span>Create Organization</span>
        </button>
      </div>

      {/* Organizations List */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
        {organizations.map((org) => (
          <div key={org.id} className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-transparent p-4 sm:p-5 hover:shadow-md dark:hover:shadow-purple-500/10 transition-all duration-200">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 bg-blue-50 dark:bg-blue-900/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <BuildingOfficeIcon className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">{org.name}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{org.slug}</p>
                </div>
              </div>
              <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full flex-shrink-0 ml-2 ${
                org.status === 'active'
                  ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                  : org.status === 'suspended'
                  ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                  : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
              }`}>
                {org.status}
              </span>
            </div>

            {org.description && (
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">{org.description}</p>
            )}

            <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-3 text-xs text-gray-500 dark:text-gray-400">
              {org.domain && (
                <p className="col-span-2"><span className="font-medium text-gray-600 dark:text-gray-300">Domain:</span> {org.domain}</p>
              )}
              <p><span className="font-medium text-gray-600 dark:text-gray-300">Admins:</span> {org.adminCount}{org.pendingCount > 0 && <span className="text-amber-600 dark:text-amber-400"> +{org.pendingCount}</span>}</p>
              <p><span className="font-medium text-gray-600 dark:text-gray-300">Managers:</span> {org.settings?.maxManagers || '—'}</p>
              <p><span className="font-medium text-gray-600 dark:text-gray-300">Students:</span> {org.settings?.maxStudents || '—'}</p>
              <p><span className="font-medium text-gray-600 dark:text-gray-300">Created:</span> {org.createdAt ? new Date(org.createdAt).toLocaleDateString() : '—'}</p>
            </div>

            <div className="flex items-center gap-1.5 pt-3 border-t border-gray-100 dark:border-gray-700/50">
              {(org.adminCount > 0 || org.pendingCount > 0) ? (
                <button
                  onClick={() => openViewAdminsModal(org)}
                  className="flex-1 flex items-center justify-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-xl transition-all duration-200"
                >
                  <UsersIcon className="w-3.5 h-3.5" />
                  <span>Admins ({org.adminCount})</span>
                </button>
              ) : (
                <button
                  onClick={() => openCreateAdminModal(org)}
                  className="flex-1 flex items-center justify-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-xl transition-all duration-200"
                >
                  <UserPlusIcon className="w-3.5 h-3.5" />
                  <span>Add Admin</span>
                </button>
              )}
              <button
                onClick={() => openEditModal(org)}
                className="p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-lg transition-all duration-200"
                title="Edit"
              >
                <PencilIcon className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => org.status === 'active' ? handleUpdateStatus(org, 'suspended') : handleUpdateStatus(org, 'active')}
                className="px-2.5 py-1.5 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-lg transition-all duration-200"
                title={org.status === 'active' ? 'Suspend' : 'Activate'}
              >
                {org.status === 'active' ? 'Suspend' : 'Activate'}
              </button>
              <button
                onClick={() => setVideoAllowanceOrg(org)}
                className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg transition-all duration-200 ${
                  org.videoSessions?.enabled
                    ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                }`}
                title={
                  org.videoSessions?.enabled
                    ? `Video sessions on — ${org.videoSessions?.monthlyMinutes ?? 0} min/month. Click to manage allowance and usage.`
                    : 'Video sessions off. Click to enable this paid add-on and set an allowance.'
                }
              >
                <VideoCameraIcon className="w-3.5 h-3.5" />
                {org.videoSessions?.enabled ? `${org.videoSessions?.monthlyMinutes ?? 0} min` : 'Video off'}
              </button>
              <button
                onClick={() => handleDeleteOrganization(org)}
                className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200"
                title="Delete"
              >
                <TrashIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {organizations.length === 0 && (
        <div className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-transparent p-6 text-center">
          <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
            <BuildingOfficeIcon className="w-6 h-6 text-gray-400 dark:text-gray-500" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">No organizations yet</h3>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">Create your first organization to get started</p>
          <button
            onClick={() => { setShowCreateModal(true); setSlugManuallyEdited(false); }}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-xl font-semibold text-xs transition-all duration-200"
          >
            <PlusIcon className="w-4 h-4" />
            Create Organization
          </button>
        </div>
      )}

      {videoAllowanceOrg && (
        <VideoAllowanceModal
          org={videoAllowanceOrg}
          onClose={() => setVideoAllowanceOrg(null)}
          onSaved={(settings) =>
            setOrganizations(prev =>
              prev.map(o => (o.id === videoAllowanceOrg.id ? { ...o, videoSessions: settings } : o)),
            )
          }
        />
      )}

      {/* Create Organization Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-transparent p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Create New Organization</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleCreateOrganization} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Organization Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    if (!slugManuallyEdited) {
                      const slug = name
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '-')
                        .replace(/^-|-$/g, '');
                      setFormData(prev => ({ ...prev, name, slug }));
                    } else {
                      setFormData(prev => ({ ...prev, name }));
                    }
                  }}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Acme Corporation"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Slug (URL identifier) *
                </label>
                <input
                  type="text"
                  required
                  value={formData.slug}
                  onChange={(e) => {
                    setSlugManuallyEdited(true);
                    setFormData(prev => ({ ...prev, slug: e.target.value.toLowerCase() }));
                  }}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="acme-corp"
                  pattern="[a-z0-9\-]+"
                  title="Lowercase letters, numbers, and hyphens only"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Lowercase, alphanumeric, hyphens only</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief description of the organization"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Domain (optional)
                </label>
                <input
                  type="text"
                  value={formData.domain}
                  onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="acme.com"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Max Managers
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.maxManagers}
                    onChange={(e) => setFormData({ ...formData, maxManagers: parseInt(e.target.value) })}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Max Students
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.maxStudents}
                    onChange={(e) => setFormData({ ...formData, maxStudents: parseInt(e.target.value) })}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-2xl hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-5 py-2.5 bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-2xl font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Creating...' : 'Create Organization'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}



      {/* Edit Organization Modal */}
      {showEditModal && selectedOrg && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setShowEditModal(false); setSelectedOrg(null); }}>
          <div className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-transparent p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Edit Organization</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedOrg(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl">
              <p className="text-sm text-blue-900 dark:text-blue-200">
                <span className="font-medium">Slug:</span> {selectedOrg.slug}
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                The slug cannot be changed after creation
              </p>
            </div>

            <form onSubmit={handleUpdateOrganization} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Organization Name *
                </label>
                <input
                  type="text"
                  required
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Acme Corporation"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Description
                </label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief description of the organization"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Domain (optional)
                </label>
                <input
                  type="text"
                  value={editFormData.domain}
                  onChange={(e) => setEditFormData({ ...editFormData, domain: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="acme.com"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Max Managers
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={editFormData.maxManagers}
                    onChange={(e) => setEditFormData({ ...editFormData, maxManagers: parseInt(e.target.value) })}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Max Students
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={editFormData.maxStudents}
                    onChange={(e) => setEditFormData({ ...editFormData, maxStudents: parseInt(e.target.value) })}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedOrg(null);
                  }}
                  className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-2xl hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-5 py-2.5 bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-2xl font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Updating...' : 'Update Organization'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Admins Modal */}
      {showViewAdminsModal && selectedOrg && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setShowViewAdminsModal(false); setSelectedOrg(null); }}>
          <div className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-transparent p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Organization Administrators</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{selectedOrg.name}</p>
              </div>
              <button
                onClick={() => {
                  setShowViewAdminsModal(false);
                  setSelectedOrg(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Add Admin Button */}
            <div className="mb-4">
              <button
                onClick={() => {
                  setShowViewAdminsModal(false);
                  openCreateAdminModal(selectedOrg);
                }}
                className="flex items-center gap-2 px-5 py-2.5 bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-2xl font-semibold text-sm transition-all duration-200"
              >
                <UserPlusIcon className="w-5 h-5" />
                Add New Admin
              </button>
            </div>

            {/* Active Admins Section */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Active Administrators ({selectedOrg.admins.length})</h3>
              {selectedOrg.admins.length === 0 ? (
                <div className="text-center py-6 bg-gray-50 dark:bg-gray-700/30 rounded-2xl">
                  <ShieldCheckIcon className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600 dark:text-gray-400 text-sm">No active administrators yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedOrg.admins.map((admin) => (
                    <div
                      key={admin.id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-gray-50 dark:bg-gray-700/30 rounded-2xl border border-gray-200 dark:border-gray-600 gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center mb-1">
                          <ShieldCheckIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2 flex-shrink-0" />
                          <h4 className="font-semibold text-gray-900 dark:text-white truncate">{admin.displayName}</h4>
                          <span className={`ml-3 px-2 py-0.5 text-xs rounded-full flex-shrink-0 ${
                            admin.status === 'active'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                              : admin.status === 'suspended'
                              ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'
                              : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400'
                          }`}>
                            {admin.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{admin.email}</p>
                        <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-500 dark:text-gray-500">
                          <span>Created: {formatDate(admin.createdAt)}</span>
                          {admin.lastLogin && (
                            <span>Last Login: {formatDate(admin.lastLogin)}</span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 flex-shrink-0">
                        {admin.status === 'active' ? (
                          <button
                            onClick={() => handleUpdateAdminStatus(admin, 'suspended')}
                            className="px-3 py-1.5 text-sm bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-2xl hover:bg-yellow-100 dark:hover:bg-yellow-900/50 transition-colors"
                            title="Suspend Admin"
                          >
                            Suspend
                          </button>
                        ) : (
                          <button
                            onClick={() => handleUpdateAdminStatus(admin, 'active')}
                            className="px-3 py-1.5 text-sm bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-2xl hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
                            title="Activate Admin"
                          >
                            Activate
                          </button>
                        )}
                        <button
                          onClick={() => handleRemoveAdmin(admin)}
                          className="px-3 py-1.5 text-sm bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-2xl hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                          title="Remove Admin"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pending Invitations Section */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Pending Invitations ({selectedOrg.pendingInvitations.length})</h3>
              {selectedOrg.pendingInvitations.length === 0 ? (
                <div className="text-center py-6 bg-gray-50 dark:bg-gray-700/30 rounded-2xl">
                  <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-2">
                    <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">No pending invitations</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedOrg.pendingInvitations.map((invitation) => (
                    <div
                      key={invitation.id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-amber-50 dark:bg-amber-900/20 rounded-2xl border border-amber-200 dark:border-amber-700 gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center mb-1">
                          <div className="w-5 h-5 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mr-2 flex-shrink-0">
                            <svg className="w-3 h-3 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
                          </div>
                          <h4 className="font-semibold text-gray-900 dark:text-white truncate">{invitation.displayName || invitation.email}</h4>
                          <span className="ml-3 px-2 py-0.5 text-xs rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-400 flex-shrink-0">
                            Pending
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{invitation.email}</p>
                        <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-500 dark:text-gray-500">
                          <span>Invited: {formatDate(invitation.createdAt)}</span>
                          <span>Expires: {new Date(invitation.expiresAt).toLocaleDateString()}</span>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleResendInvitation(invitation)}
                          className="px-3 py-1.5 text-sm bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-2xl hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                          title="Resend Invitation"
                        >
                          Resend
                        </button>
                        <button
                          onClick={() => handleRemoveInvitation(invitation)}
                          className="px-3 py-1.5 text-sm bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-2xl hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                          title="Cancel Invitation"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowViewAdminsModal(false);
                  setSelectedOrg(null);
                }}
                className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-2xl hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Org-Admin Modal */}
      {showCreateAdminModal && selectedOrg && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setShowCreateAdminModal(false); setSelectedOrg(null); }}>
          <div className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-transparent p-6 max-w-md w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Create Org Admin</h2>
              <button
                onClick={() => {
                  setShowCreateAdminModal(false);
                  setSelectedOrg(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl">
              <p className="text-sm text-blue-900 dark:text-blue-200">
                <span className="font-medium">Organization:</span> {selectedOrg.name}
              </p>
            </div>

            <form onSubmit={handleCreateOrgAdmin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  value={adminFormData.email}
                  onChange={(e) => setAdminFormData({ ...adminFormData, email: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="admin@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Display Name *
                </label>
                <input
                  type="text"
                  required
                  value={adminFormData.displayName}
                  onChange={(e) => setAdminFormData({ ...adminFormData, displayName: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="John Doe"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="sendInvitation"
                  checked={adminFormData.sendInvitation}
                  onChange={(e) => setAdminFormData({ ...adminFormData, sendInvitation: e.target.checked })}
                  className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                />
                <label htmlFor="sendInvitation" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Send invitation email
                </label>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                The admin will receive an email to sign in and complete their profile setup. The invitation expires in 7 days.
              </p>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateAdminModal(false);
                    setSelectedOrg(null);
                  }}
                  className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-2xl hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-5 py-2.5 bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-2xl font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Creating...' : 'Create Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

export default OrganizationManagement;
